import { Dispatch, SetStateAction, useCallback } from 'react';
import Grid from '@mui/material/Grid';
import { Control, FieldErrors, UseFormSetValue } from 'react-hook-form';
import { Button, Box, CircularProgress } from '@mui/material';
import TextField from '@mui/material/TextField';
import { useMutation } from '@tanstack/react-query';

import { ValidateForm } from '../utils/interfaces/validateForm';
import ControlledTextInput from 'ui/components/inputs/controlled-text-input';
import ControlledDesktopDatePickerInput from 'ui/components/inputs/controlled-desktop-date-picker-input';
import theme from '../styles/theme';
import { validateId } from '../http/api/users';
import { DateTime } from 'luxon';

interface IValidateUserIdentificationFormProps {
  identification: string;
  setDisabledContactValues: Dispatch<SetStateAction<boolean>>;
  disabledContactValues: boolean;
  setValue: UseFormSetValue<ValidateForm>;
  expeditionDate: DateTime<true> | null;
  setDisabledIdentification: Dispatch<SetStateAction<boolean>>;
  setSearchValue: UseFormSetValue<{ identification: string }>;
  control: Control<ValidateForm>;
  errors: FieldErrors<ValidateForm>;
  dirtyFields: Partial<
    Readonly<{
      name?: boolean;
      lastName?: boolean;
      expeditionDate?: boolean;
      phone?: boolean;
      otp?: boolean;
      hasContract?: boolean;
      contract?: boolean;
      contractRelationship?: boolean;
    }>
  >;
}

const ValidateUserIdentificationForm = (
  props: IValidateUserIdentificationFormProps
) => {
  const {
    identification,
    setDisabledContactValues,
    disabledContactValues,
    setValue,
    expeditionDate,
    control,
    errors,
    dirtyFields,
    setDisabledIdentification,
    setSearchValue,
  } = props;

  const validateIdMutation = useMutation({
    mutationFn: validateId,
    onSuccess: (data) => {
      if (data.data.identificationValidationId !== null) {
        setValue('name', data.data.clientName);
        setValue('lastName', data.data.clientLastName);
        setDisabledContactValues(false);
        setValue('editContract', true);
      }
    },
  });

  const onValidateIdentification = useCallback(async () => {
    if (!expeditionDate || errors.expeditionDate) {
      return;
    }

    setSearchValue('identification', identification);
    setDisabledIdentification(true);

    validateIdMutation.mutateAsync({
      identification,
      expeditionDate: expeditionDate.toISO(),
    });
  }, [
    expeditionDate,
    identification,
    validateIdMutation,
    errors.expeditionDate,
    setDisabledIdentification,
    setSearchValue,
  ]);

  return (
    <Grid
      container
      direction="row"
      rowSpacing={2}
      columnSpacing={4}
      justifyContent="center"
      alignItems="flex-start"
    >
      <Grid>
        <TextField
          id="identification"
          label="Cédula"
          value={identification}
          InputProps={{
            disabled: true,
          }}
          sx={{
            width: '300px',
          }}
        />
      </Grid>
      <Grid>
        <Box display="flex" justifyContent="flex-end">
          <ControlledDesktopDatePickerInput
            label="Fecha expedición"
            name="expeditionDate"
            control={control}
            onEnterKeyPress={onValidateIdentification}
            displayFormat="dd/MM/yyyy"
            inputsProps={{
              disabled: !disabledContactValues,
            }}
            errors={errors.expeditionDate}
            isDirty={Boolean(dirtyFields?.expeditionDate)}
            autofocus={true}
            resetKey={identification}
            slotProps={{
              textField: {
                sx: {
                  width: 192,
                },
              },
            }}
          />
          <Button
            variant="outlined"
            disabled={
              !expeditionDate ||
              Boolean(errors.expeditionDate) ||
              !disabledContactValues ||
              validateIdMutation.isPending
            }
            onClick={onValidateIdentification}
            sx={{
              height: 55,
              width: 100,
              marginLeft: theme.spacing(1),
              fontSize: 'small',
              textTransform: 'none',
              padding: theme.spacing(1.5),
              fontWeight: '600',
            }}
          >
            {validateIdMutation.isPending ? (
              <CircularProgress size={25} color="inherit" />
            ) : (
              'Validar'
            )}
          </Button>
        </Box>
      </Grid>
      <Grid>
        <ControlledTextInput
          label="Nombres"
          name="name"
          control={control}
          type="text"
          inputsProps={{
            sx: {
              width: '300px',
            },
            disabled: true,
          }}
          errors={errors.name}
          isDirty={Boolean(dirtyFields?.name)}
        />
      </Grid>
      <Grid>
        <ControlledTextInput
          label="Apellidos"
          name="lastName"
          control={control}
          type="text"
          inputsProps={{
            sx: {
              width: '300px',
            },
            disabled: true,
          }}
          errors={errors.lastName}
          isDirty={Boolean(dirtyFields?.lastName)}
        />
      </Grid>
    </Grid>
  );
};

export default ValidateUserIdentificationForm;
